/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  CircularProgress,
  Typography,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Box,
  Stack,
} from '@mui/material';
import {memo, useState, ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import RemoveIcon from '@mui/icons-material/Remove'; //マテリアルUIコンポーネント ×ボタン

//MTH component import
import OperationHeader from './ButtonAppBar'; //機器操作画面 上部バー
import RunModeButtonGroup from './RunModeButtonGroup'; //運転モードボタン群
import AirflowButtonGroup from './AirflowButtonGroup'; //風量設定ボタン群
import PresetTempSlider from './PresetTempSlider'; //設定温度変更スライダー
import {PrimaryButton, SecondaryButton} from './BasicButton';
//API import
import {postCommandsSites} from '../API/API_Collection';
//interface import
import * as apiIF from '../Type/api';
//Style import
import '../css/common/common.css';

import {useInteractJS} from '../functions/positionMove'; //コンポーネント移動
import {invertComma, initialUpper, roundOff} from '../functions/convertString'; //コンマ・ピリオド変換、文字列の先頭を大文字、後ろを小文字に変換
import {useOperationStates} from '../hooks/states/useOperationStates';
import {iconBlack, iconWhite, iconYellow} from '../define';

/**
 * 空調機指令ダイアログ
 * @param {string} ope_target　指令対象(site,floors,groups,sl)
 * @param {string} ope_targetID　指令対象のID
 * @param {string} ope_SiteID　　サイトID
 * @param {() => void} operationCloseClick 指令ダイアログの開閉
 */
const AirconOperation = memo(
  (props: {
    operationProps: {
      opeTarget: apiIF.reqParameters.postCommandsSitesQuery['mode'] | undefined;
      opeTargetID: string;
      opeSiteID: string;
      operationCloseClick: () => void;
    };
  }) => {
    const trans = useTranslation().t; //翻訳関数

    const {opeTarget, opeTargetID, opeSiteID, operationCloseClick} =
      props.operationProps;

    /**
     * 運転モードのデザインの決定
     * @param mode 運転モード
     * @returns デザインのパラメータ
     */
    const modeStyle = (
      mode: 'auto' | 'cooling' | 'heating' | 'fan' | 'dehumidify' | undefined
    ) => {
      switch (mode) {
        case 'cooling':
          return {
            src: iconWhite.COOLING.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.COOLING.alt}`
            ),
            class:
              'operation__card__content__state__value__iconBorder--mode--cooling',
            displayChar: trans('AirconOperation.item.value.mode.cooling'),
          };
        case 'heating':
          return {
            src: iconWhite.HEATING.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.HEATING.alt}`
            ),
            class:
              'operation__card__content__state__value__iconBorder--mode--heating',
            displayChar: trans('AirconOperation.item.value.mode.heating'),
          };
        case 'fan':
          return {
            src: iconWhite.BLOWER.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.BLOWER.alt}`
            ),
            class:
              'operation__card__content__state__value__iconBorder--mode--fan',
            displayChar: trans('AirconOperation.item.value.mode.fan'),
          };
        case 'dehumidify':
          return {
            src: iconWhite.DRYING.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.DRYING.alt}`
            ),
            class:
              'operation__card__content__state__value__iconBorder--mode--dry',
            displayChar: trans('AirconOperation.item.value.mode.dry'),
          };
        case 'auto':
          return {
            src: iconWhite.AUTO.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.AUTO.alt}`
            ),
            class:
              'operation__card__content__state__value__iconBorder--mode--auto',
            displayChar: trans('AirconOperation.item.value.mode.auto'),
          };
        default:
          return {
            src: undefined,
            alt: undefined,
            class: undefined,
            displayChar: undefined,
          };
      }
    };
    /**
     * 風量のデザインの決定
     * @param flow 風量
     * @returns デザインのパラメータ
     */
    const airFlow = (
      flow: 'auto' | 'high' | 'low' | 'middle' | 'powerful' | undefined
    ) => {
      switch (flow) {
        case 'auto':
          return {
            src: iconWhite.FAN_SPEED_AUTO.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.FAN_SPEED_AUTO.alt}`
            ),
            displayChar: trans('AirconOperation.item.value.fanSpeed.auto'),
          };
        case 'low':
          return {
            src: iconWhite.FAN_SPEED_1.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.FAN_SPEED_1.alt}`
            ),
            displayChar: trans('AirconOperation.item.value.fanSpeed.low'),
          };
        case 'middle':
          return {
            src: iconWhite.FAN_SPEED_2.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.FAN_SPEED_2.alt}`
            ),
            displayChar: trans('AirconOperation.item.value.fanSpeed.middle'),
          };
        case 'high':
          return {
            src: iconWhite.FAN_SPEED_3.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.FAN_SPEED_3.alt}`
            ),
            displayChar: trans('AirconOperation.item.value.fanSpeed.high'),
          };
        case 'powerful':
          return {
            src: iconWhite.FAN_SPEED_4.src,
            alt: trans(
              `AirconOperation.image.alternativeText.${iconWhite.FAN_SPEED_4.alt}`
            ),
            displayChar: trans('AirconOperation.item.value.fanSpeed.powerful'),
          };
        default:
          return {
            src: undefined,
            alt: undefined,
            class: undefined,
            displayChar: undefined,
          };
      }
    };

    //空調機指令確認ダイアログ　開閉
    const [isOpenDialog, setIsOpenDialog] = useState(false);
    //現在の運転状態
    const [nowRunStatus, setNowRunStatus] = useState('');
    //prettier-ignore
    //処理待ち
    const [operationWaitProgress, setOperationWaitProgress] = useState(false);
    //登録ユニットが無い場合に操作画面を無効メッセージを表示する
    const [airconOperationFlag, setAirconOperationFlag] = useState(true);

    /** 指令情報 */
    const [operationTemp, setOperationTemp] = useState<number>(23); //設定温度 指令値
    //prettier-ignore
    const [operationMode, setOperationMode] = useState<apiIF.reqParameters.postCommandsSitesBody['operation-mode']>(undefined); //運転モード 指令値
    //prettier-ignore
    const [operationFlow, setOperationFlow] = useState<apiIF.reqParameters.postCommandsSitesBody['air-flow']>(undefined); //風量 指令値

    //運転状態を取得するカスタムフック
    const {
      operationPower, //運転/停止の表示(指令ボタンの役割も兼用)
      dispIndoorTemp, //室内温度の表示
      dispPresetTemp, //設定温度の表示
      dispOpeMode, //運転モードの表示
      dispFlow, //風量の表示
      dispError, //エラーコードの表示
      operationDataSet, //設定温度,運転モード,風量を指令時に表示を更新する関数
      runDispDataSet, //運転ボタン押下時に運転表示を更新する関数
    } = useOperationStates(opeSiteID, opeTarget, opeTargetID);
    const {
      //運転モードアイコン画像
      src: modeIconSrc,
      //風量設定Art
      alt: modeIconArt,
      //運転モード文字列
      displayChar: modeDisplay,
      //運転モード クラス名
      class: modeIconClassName,
    } = modeStyle(dispOpeMode);
    const {
      //風量設定アイコン画像
      src: airFlowIconSrc,
      //風量設定Art
      alt: airFlowIconArt,
      //風量設定文字列
      displayChar: airFlowDisplay,
    } = airFlow(dispFlow);

    const interact = useInteractJS();

    const runWait = () => {
      setOperationWaitProgress(false);
    };
    /* APPLYまたはPOWERが押下された場合の処理 */
    const handleClickOpen = (runTemp?: string) => {
      if (runTemp === '') runTemp = 'stop';

      if (opeTarget === 'sl') {
        //個別操作の場合は、ダイアログの確認無しで操作指令を実行する
        if (runTemp !== undefined) {
          //POWER押下時用の指令処理
          setRunStatusFunc(runTemp);
        } else {
          //APPLY押下時用の指令処理
          operationApply();
        }
        //３秒間、運転ボタン/APPLYボタンをDisableとする
        setOperationWaitProgress(true);
        setTimeout(runWait, 3000);
      } else {
        //個別操作以外の場合は、指令操作を実行する前にダイアログで確認を行う
        if (runTemp !== undefined) {
          //POWER押下時は変更後の運転状態を引数として、stateに保存
          setNowRunStatus(runTemp);
        } else {
          //APPLY押下時は、stateを空欄とする
          setNowRunStatus('');
        }
        setIsOpenDialog(true);
      }
    };
    const handleClose = () => {
      setIsOpenDialog(false);
    };

    /* ダイアログでOK押下時の処理 */
    const runActionHandle = () => {
      //NowRunStatusが空欄の場合は、APPLY側の処理
      if (nowRunStatus === '') {
        //APPLY押下時用の指令処理
        operationApply();
      } else {
        //POWER押下時用の指令処理
        setRunStatusFunc(nowRunStatus);
      }
      handleClose();
      //３秒間、運転ボタン/APPLYボタンをDisableとする
      setOperationWaitProgress(true);
      setTimeout(runWait, 3000);
    };

    /**
     * POWER押下時用の指令処理
     * @param tempNowRunStatus
     */
    const setRunStatusFunc = (tempNowRunStatus: string) => {
      /** 指令する運転状態 */
      let runStatus: apiIF.reqParameters.postCommandsSitesBody['run-stop'];
      // 現在の運転状態とは逆の状態を指令する
      if (tempNowRunStatus === 'run') {
        runDispDataSet('stop');
        runStatus = 'stop';
      } else {
        runDispDataSet('run');
        runStatus = 'run';
      }
      const event = new Date();
      const opeData: apiIF.reqParameters.postCommandsSitesBody = {
        'date': event.toISOString(),
        'run-stop': runStatus,
      };

      postCommandsSites(opeSiteID, opeData, {
        mode: opeTarget,
        target: opeTargetID,
      })
        .then(response => {})
        .catch(err => {
          if (err.response.data.code === 'INSUFFICIENT_PERMISSIONS') {
            alert(trans('AirconOperation.alertMsg.noAuthority'));
          } else {
            alert(trans('AirconOperation.alertMsg.operationErr'));
          }
        });
    };

    /**
     * APPLY押下時用の指令処理
     */
    const operationApply = () => {
      //状態値を指令値に書き換える
      operationDataSet(String(operationTemp), operationMode, operationFlow);

      const event = new Date();
      const opeData: apiIF.reqParameters.postCommandsSitesBody = {
        date: event.toISOString(),
      };
      if (operationMode !== undefined) {
        opeData['operation-mode'] = operationMode;
      }
      if (operationTemp !== 0) {
        opeData['preset-temperature'] = String(operationTemp.toFixed(1));
      }
      if (operationFlow !== undefined) {
        opeData['air-flow'] = operationFlow;
      }

      postCommandsSites(opeSiteID, opeData, {
        mode: opeTarget,
        target: opeTargetID,
      })
        .then(response => {})
        .catch(err => {
          if (err.response.data.code === 'INSUFFICIENT_PERMISSIONS') {
            alert(trans('AirconOperation.alertMsg.noAuthority'));
          } else {
            alert(trans('AirconOperation.alertMsg.operationErr'));
          }
        });
    };

    /**
     * エラーコードに基づいてエラー出力とアイコンを表示するコンポーネント。
     *
     * @param errorCode - 受信したエラーコード
     * @returns {JSX.Element} エラーコードに基づいた表示要素。
     */
    const ErrorDisplay = (errorCode: string) => {
      if (errorCode === '') {
        return <></>; //無課金時
      } else if (errorCode === '00') {
        return <RemoveIcon id="mv-txtOperationError" />;
      } else if (errorCode === '100') {
        //通信異常時
        return (
          <Box
            id="mv-txtOperationError"
            className="operation__card__content__state__value--errorCode">
            {trans('AirconOperation.item.value.errCode.disconnect')}
          </Box>
        );
      } else {
        return (
          <Grid container alignItems="center" columnSpacing={1}>
            <Grid item xs="auto">
              <Box className="operation__card__content__state__value__iconBorder--errorCode">
                <img
                  id="mv-txtOperationError"
                  className="operation__card__content__state__value__iconBorder__image"
                  src={iconYellow.ERROR.src}
                  alt={`${trans(
                    `AirconOperation.image.alternativeText.${iconYellow.ERROR.alt}`
                  )}`}
                />
              </Box>
            </Grid>
            <Grid
              item
              xs
              className="operation__card__content__state__value--errorCode">
              {trans('AirconOperation.item.value.errCode.code')}
              {errorCode}
            </Grid>
          </Grid>
        );
      }
    };

    return (
      <>
        <Paper
          elevation={10}
          ref={interact.ref}
          style={{...interact.style}}
          className="operation">
          <Card className="operation__card">
            <CardHeader
              disableTypography
              title={
                <OperationHeader
                  opeTarget={opeTarget}
                  opeTargetID={opeTargetID}
                  opeSiteID={opeSiteID}
                  operationCloseClick={operationCloseClick}
                />
              }
              className="operation__card__header"
            />
            <CardContent>
              {airconOperationFlag ? (
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={3}
                  alignItems="flex-end"
                  className="operation__card__content">
                  {/** 状態値 */}
                  {/** 運転/停止 */}
                  <Grid item xs={2}>
                    <Stack spacing={1}>
                      <Box className="operation__card__content__state__name operation__card__content__state__name--runStop">
                        {trans('AirconOperation.item.title.power')}
                      </Box>
                      <Box className="operation__card__content__state__value">
                        <IconButton
                          disabled={operationWaitProgress}
                          title={`${trans(
                            `AirconOperation.image.title.power.${operationPower}`,
                            {defaultValue: ''} //operationPowerが'run' or 'stop'以外の場合
                          )}`}
                          onClick={() => handleClickOpen(operationPower)}>
                          {operationPower === 'run' ? (
                            <Box className="operation__card__content__state__value__runStopButton--run">
                              <img
                                id="mv-btnAcOperationRunStop"
                                src={iconWhite.POWER.src}
                                alt={`${trans(
                                  `AirconOperation.image.alternativeText.power.${iconWhite.POWER.alt}`
                                )}`}
                                title={`${trans(
                                  `AirconOperation.image.title.power.${iconWhite.POWER.title}`
                                )}`}
                                className="operation__card__content__state__value__runStopButton__image"
                              />
                            </Box>
                          ) : (
                            <Box className="operation__card__content__state__value__runStopButton--stop">
                              <img
                                id="mv-btnAcOperationRunStop"
                                src={iconBlack.POWER.src}
                                alt={`${trans(
                                  `AirconOperation.image.alternativeText.power.${iconBlack.POWER.alt}`
                                )}`}
                                title={`${trans(
                                  `AirconOperation.image.title.power.${iconBlack.POWER.title}`
                                )}`}
                                className="operation__card__content__state__value__runStopButton__image"
                              />
                            </Box>
                          )}
                        </IconButton>
                      </Box>
                    </Stack>
                  </Grid>
                  {/** 室内温度 */}
                  <Grid item xs={2}>
                    <StateContents
                      title={trans('AirconOperation.item.title.roomTemp')}
                      value={
                        <>
                          {dispIndoorTemp === ''
                            ? '--'
                            : invertComma(roundOff(dispIndoorTemp, 1))}
                          <Box
                            component="span"
                            className="operation__card__content__state__value__degC">
                            &nbsp;
                            {trans('AirconOperation.item.value.temp.unit')}
                          </Box>
                        </>
                      }
                    />
                  </Grid>
                  {/** 設定温度 */}
                  <Grid item xs={2}>
                    <StateContents
                      title={trans('AirconOperation.item.title.setTemp')}
                      value={
                        <>
                          {dispPresetTemp === ''
                            ? '--'
                            : invertComma(roundOff(dispPresetTemp, 1))}
                          <Box
                            component="span"
                            className="operation__card__content__state__value__degC">
                            &nbsp;
                            {trans('AirconOperation.item.value.temp.unit')}
                          </Box>
                        </>
                      }
                    />
                  </Grid>
                  {/** 運転モード */}
                  <Grid item xs={2}>
                    <StateContents
                      title={trans('AirconOperation.item.title.mode')}
                      value={
                        modeDisplay === undefined ? (
                          <Box className="operation__card__content__state__value">
                            --
                          </Box>
                        ) : (
                          <Grid container alignItems="center" columnSpacing={1}>
                            <Grid item xs="auto">
                              <Box className={modeIconClassName}>
                                <img
                                  id="mv-iconOperationMode"
                                  className="operation__card__content__state__value__iconBorder__image"
                                  src={modeIconSrc}
                                  alt={modeIconArt}
                                />
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs
                              className="operation__card__content__state__value--mode">
                              {modeDisplay}
                            </Grid>
                          </Grid>
                        )
                      }
                    />
                  </Grid>
                  {/** 風量 */}
                  <Grid item xs={2}>
                    <StateContents
                      title={trans('AirconOperation.item.title.fanSpeed')}
                      value={
                        airFlowDisplay === undefined ? (
                          <Box className="operation__card__content__state__value--fanError">
                            --
                          </Box>
                        ) : (
                          <Grid
                            container
                            alignItems="center"
                            columnSpacing={0.5}>
                            <Grid item xs="auto">
                              <Box className="operation__card__content__state__value__iconBorder--fan">
                                <img
                                  id="mv-iconOperationAirFlow"
                                  className="operation__card__content__state__value__iconBorder__image"
                                  src={airFlowIconSrc}
                                  alt={airFlowIconArt}
                                />
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs
                              className="operation__card__content__state__value--fan">
                              {initialUpper(airFlowDisplay)}
                            </Grid>
                          </Grid>
                        )
                      }
                    />
                  </Grid>
                  {/** エラーコード */}
                  <Grid item xs={2}>
                    <StateContents
                      title={trans('AirconOperation.item.title.errCode')}
                      value={ErrorDisplay(dispError)}
                    />
                  </Grid>
                  {/**  指令ボタン */}
                  <Grid item xs={12} container alignItems="center">
                    <Grid
                      item
                      xs
                      className="operation__card__content__borderline"></Grid>
                    <Grid item xs="auto">
                      <PrimaryButton
                        disabled={operationWaitProgress}
                        onClick={() => handleClickOpen()}
                        className="operation__card__content__applyButton"
                        startIcon={
                          operationWaitProgress ? (
                            <CircularProgress size={20} color="info" />
                          ) : (
                            <img
                              src={iconWhite.ARROW_UP.src}
                              alt={`${trans(
                                `AirconOperation.image.alternativeText.${iconWhite.ARROW_UP.alt}`
                              )}`}
                              className="operation__card__content__applyButton__icon"
                            />
                          )
                        }
                        buttonText={trans('AirconOperation.buttonTxt.apply')}
                      />
                    </Grid>
                    <Grid
                      item
                      xs
                      className="operation__card__content__borderline"></Grid>
                  </Grid>
                  {/** 設定値 */}
                  {/** 設定温度 */}
                  <Grid item xs={4}>
                    <SettingContents
                      title={trans('AirconOperation.item.title.setTemp')}
                      value={
                        <PresetTempSlider
                          displayPresetTemp={Number(dispPresetTemp)}
                          operationMode={operationMode}
                          setOperationTemp={setOperationTemp}
                        />
                      }
                    />
                  </Grid>
                  {/** 運転モード */}
                  <Grid item xs={4}>
                    <SettingContents
                      title={trans('AirconOperation.item.title.mode')}
                      value={
                        <RunModeButtonGroup
                          displayMode={dispOpeMode}
                          setOpeMode={setOperationMode}
                        />
                      }
                    />
                  </Grid>
                  {/** 風量 */}
                  <Grid item xs={4}>
                    <SettingContents
                      title={trans('AirconOperation.item.title.fanSpeed')}
                      value={
                        <AirflowButtonGroup
                          displayFlow={dispFlow}
                          setOpeFlow={setOperationFlow}
                        />
                      }
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography marginLeft={2}>
                  {trans('AirconOperation.errMsg.noRegistrationUnit')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Paper>
        <Dialog
          open={isOpenDialog}
          onClose={handleClose}
          aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">
            {trans('AirconOperation.dialogBox.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {opeTarget === 'site'
                ? trans('AirconOperation.dialogBox.msg.site')
                : trans('AirconOperation.dialogBox.msg.nonSite')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <SecondaryButton
              onClick={handleClose}
              buttonText={trans('AirconOperation.dialogBox.buttonTxt.cancel')}
            />
            <PrimaryButton
              onClick={runActionHandle}
              buttonText={trans('AirconOperation.dialogBox.buttonTxt.ok')}
            />
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

/**
 * 状態値
 * @param props props
 * - title タイトル
 * - value 状態値
 * @returns JSX
 */
const StateContents = (props: {title: string; value: ReactElement}) => {
  return (
    <Stack spacing={1}>
      <Box className="operation__card__content__state__name">{props.title}</Box>
      <Box className="operation__card__content__state__value">
        {props.value}
      </Box>
    </Stack>
  );
};

/**
 * 設定値
 * @param props props
 * - title タイトル
 * - value 設定値
 * @returns JSX
 */
const SettingContents = (props: {title: string; value: ReactElement}) => {
  return (
    <Stack spacing={1}>
      <Box className="operation__card__content__setting__name">
        {props.title}
      </Box>
      <Box className="operation__card__content__setting__value">
        {props.value}
      </Box>
    </Stack>
  );
};
export default AirconOperation;
